import React, { useEffect, useLayoutEffect, useState, KeyboardEvent, useRef } from 'react';

import styleClassNames from '@styles/modules/section_bar_heading.module.scss';

import LanguageMenuDropdown from './languageMenuDropdown';

export interface SectionDetails {
  displayName: string;
  content: JSX.Element;
  onActiveSection?: () => void;
  tabClassName?: string;
}

export interface SectionBarProps {
  sections: SectionDetails[];
}

const SectionBar = (props: SectionBarProps): JSX.Element => {
  const { sections } = props;
  const [visibleSection, setVisibleSection] = useState(0);
  const tabPanelRef = useRef<HTMLDivElement>(null);
  /** When true, next commit should move focus into the tabpanel (click / Enter / Space only — not Tab between tabs). */
  const shouldFocusPanelRef = useRef(false);

  const requestFocusInPanel = (): void => {
    shouldFocusPanelRef.current = true;
  };

  const onClickHandler = (targetSection: number): void => {
    requestFocusInPanel();
    if (targetSection === visibleSection) {
      queueMicrotask(() => {
        if (!tabPanelRef.current) {
          return;
        }
        shouldFocusPanelRef.current = false;
        tabPanelRef.current.focus({ preventScroll: true });
      });
      return;
    }
    setVisibleSection(targetSection);
  };

  const onTabFocus = (index: number): void => {
    if (index !== visibleSection) {
      setVisibleSection(index);
    }
  };

  useEffect(() => {
    const callback = sections[visibleSection].onActiveSection;
    if (callback) {
      callback();
    }
  }, [sections, visibleSection]);

  // Move focus into the tabpanel only after explicit activation so Tab can move between tabs.
  useLayoutEffect(() => {
    if (!shouldFocusPanelRef.current || !tabPanelRef.current) {
      return;
    }
    shouldFocusPanelRef.current = false;
    tabPanelRef.current.focus({ preventScroll: true });
  }, [visibleSection]);

  const onTabKeyDown = (e: KeyboardEvent<HTMLLIElement>, index: number): void => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onClickHandler(index);
    }
  };

  return (
    <div>
      <div className={`section-bar-heading ${styleClassNames['section-bar-heading']}`}>
        <ul className={`${styleClassNames['section-bar-list']}`} role="tablist">
          {sections.map((section, i) => {
            const activeClass = i === visibleSection ? `t-active ${styleClassNames['active-section']}` : '';
            const itemClass = styleClassNames['section-bar-item'];
            const tabClass = section.tabClassName || '';
            const sectionId = `section-${i}`;
            return (
              <li
                key={section.displayName}
                aria-controls={sectionId}
                aria-selected={i === visibleSection}
                className={`section-bar-item ${itemClass} ${tabClass} ${activeClass}`}
                data-title={section.displayName}
                id={`tab-section-${i}`}
                onClick={(): void => onClickHandler(i)}
                onFocus={(): void => onTabFocus(i)}
                onKeyDown={(e: KeyboardEvent<HTMLLIElement>): void => onTabKeyDown(e, i)}
                role="tab"
                tabIndex={0}
              >
                {section.displayName}
              </li>
            );
          })}
        </ul>
        {BFG.multiLanguageAssetDetailsEnabled && (
          <LanguageMenuDropdown />
        )}
      </div>
      <div
        ref={tabPanelRef}
        aria-labelledby={`tab-section-${visibleSection}`}
        aria-live="polite"
        className="section-content"
        id={`section-${visibleSection}`}
        role="tabpanel"
        tabIndex={0}
      >
        {sections[visibleSection].content}
      </div>
    </div>
  );
};

export default SectionBar;
